import { useState, type ReactNode } from 'react'
import { TextInput, Loader, type TextInputProps } from '@mantine/core'
import { lookupCep, isValidCep, type CepAddress } from '@/lib/viacep'

interface CepInputProps {
  value: string
  onChange: (cep: string) => void
  /** Chamado quando o ViaCEP resolve — o pai preenche logradouro/bairro/cidade/uf (editáveis). */
  onResolved: (addr: CepAddress) => void
  label?: string
  /** Passthrough p/ formulários com tema próprio (ex.: Register dark). */
  size?: TextInputProps['size']
  styles?: TextInputProps['styles']
  leftSection?: ReactNode
}

function maskCep(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8)
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d
}

export function CepInput({ value, onChange, onResolved, label = 'CEP', size, styles, leftSection }: CepInputProps) {
  const [loading, setLoading] = useState(false)
  const [notFound, setNotFound] = useState(false)

  async function maybeLookup(masked: string) {
    const digits = masked.replace('-', '')
    if (!isValidCep(digits)) return
    setLoading(true)
    setNotFound(false)
    const addr = await lookupCep(digits)
    setLoading(false)
    if (addr) onResolved(addr)
    else setNotFound(true) // degrada p/ manual; não bloqueia
  }

  return (
    <TextInput
      label={label}
      placeholder="00000-000"
      value={value}
      maxLength={9}
      size={size}
      styles={styles}
      leftSection={leftSection}
      rightSection={loading ? <Loader size="xs" /> : undefined}
      error={notFound ? 'CEP não encontrado — preencha manualmente' : undefined}
      onChange={(e) => {
        const masked = maskCep(e.currentTarget.value)
        onChange(masked)
        if (masked.replace('-', '').length === 8) void maybeLookup(masked)
      }}
    />
  )
}
